import mbxGeocoding from "@mapbox/mapbox-sdk/services/geocoding"
import type { GeocodeFeature } from "@mapbox/mapbox-sdk/services/geocoding"
import { useNavigate } from "@tanstack/react-router"
import _ from "lodash"
import { Building2, Loader2, MapPin, Search } from "lucide-react"
import { useCallback, useEffect, useId, useRef, useState } from "react"

import { Input } from "@/components/ui/input"
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover"
import type { CompanySearchResult } from "@/db/queries/company"
import { cn } from "@/lib/utils"

import { extractLocationParams } from "./extract-location-params"

type DropdownItem =
  | { type: "location"; data: GeocodeFeature }
  | { type: "company"; data: CompanySearchResult }

export const LocationSearchForm = () => {
  const navigate = useNavigate()
  const listboxId = useId()
  const [query, setQuery] = useState("")
  const [locationResults, setLocationResults] = useState<GeocodeFeature[]>([])
  const [companyResults, setCompanyResults] = useState<CompanySearchResult[]>(
    []
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [isNavigating, setIsNavigating] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const geocodingClient = useRef(
    mbxGeocoding({ accessToken: import.meta.env.VITE_MAPBOX_PUBLIC_KEY })
  ).current

  const mapboxRequestRef = useRef<{ abort: () => void } | null>(null)
  const companyAbortRef = useRef<AbortController | null>(null)

  const allItems: DropdownItem[] = [
    ...locationResults.map((f) => ({ type: "location" as const, data: f })),
    ...companyResults.map((c) => ({ type: "company" as const, data: c })),
  ]

  const navigateToItem = useCallback(
    async (item: DropdownItem) => {
      if (item.type === "company") {
        setIsNavigating(true)
        navigate({ to: `/company/${item.data.longSlug}` })
        setIsOpen(false)
        return
      }

      setIsNavigating(true)
      setIsOpen(false)
      setErrorMessage(null)

      const params = extractLocationParams(item.data)

      try {
        const res = await fetch("/api/resolve-market", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params),
        })

        if (!res.ok) throw new Error()

        const body = await res.json()

        if (body.market?.stateSlug && body.market?.marketSlug) {
          navigate({
            to: `/solar-installers/${body.market.stateSlug}/${body.market.marketSlug}`,
          })
        } else {
          setErrorMessage(
            "No results found. Check your spelling, or try another city or 5-digit zip code."
          )
          setIsNavigating(false)
        }
      } catch {
        setErrorMessage("Something went wrong. Please try again.")
        setIsNavigating(false)
      }
    },
    [navigate]
  )

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (activeIndex >= 0 && activeIndex < allItems.length) {
        navigateToItem(allItems[activeIndex])
        return
      }
      const firstLocation = allItems.find((i) => i.type === "location")
      if (firstLocation) {
        navigateToItem(firstLocation)
      }
    },
    [activeIndex, allItems, navigateToItem]
  )

  const fetchResults = useCallback(
    async (q: string) => {
      if (q.length < 2) {
        setLocationResults([])
        setCompanyResults([])
        setIsOpen(false)
        setErrorMessage(null)
        return
      }

      setIsLoading(true)
      setErrorMessage(null)

      try {
        // Cancel stale requests
        mapboxRequestRef.current?.abort()
        companyAbortRef.current?.abort()

        const companyController = new AbortController()
        companyAbortRef.current = companyController

        const mapboxRequest = geocodingClient.forwardGeocode({
          query: q,
          countries: ["us"],
          types: ["place", "postcode"],
          autocomplete: true,
          limit: 5,
        })
        mapboxRequestRef.current = mapboxRequest
        const mapboxPromise = mapboxRequest.send()

        const companyFetch = fetch(`/api/search?q=${encodeURIComponent(q)}`, {
          signal: companyController.signal,
        })

        // Run both in parallel, handle errors independently
        const [mapboxResult, companyResult] = await Promise.allSettled([
          mapboxPromise,
          companyFetch.then((r) => r.json()),
        ])

        const locations =
          mapboxResult.status === "fulfilled"
            ? (mapboxResult.value.body?.features ?? [])
            : []
        const companies =
          companyResult.status === "fulfilled"
            ? (companyResult.value.companies ?? [])
            : []

        setLocationResults(locations)
        setCompanyResults(companies)
        const hasResults = locations.length > 0 || companies.length > 0
        setIsOpen(hasResults)
        setActiveIndex(-1)
      } finally {
        setIsLoading(false)
      }
    },
    [geocodingClient]
  )

  const debouncedFetch = useRef(
    _.debounce((q: string) => fetchResults(q), 250)
  ).current

  useEffect(() => {
    return () => {
      debouncedFetch.cancel()
      mapboxRequestRef.current?.abort()
      companyAbortRef.current?.abort()
    }
  }, [debouncedFetch])

  const handleInputChange = (value: string) => {
    setQuery(value)
    setErrorMessage(null)
    debouncedFetch(value)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || !allItems.length) return

    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((prev) => (prev < allItems.length - 1 ? prev + 1 : 0))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : allItems.length - 1))
    }
  }

  const hasNoResults = allItems.length === 0 && query.length >= 2 && !isLoading

  const activeItemId =
    activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined

  const isDropdownOpen = isOpen || hasNoResults || !!errorMessage

  return (
    <Popover open={isDropdownOpen} onOpenChange={setIsOpen}>
      <PopoverAnchor asChild>
        <div>
          <form
            className="border-4 shadow-[6px_6px_0_0_hsl(var(--brand))]"
            onSubmit={handleSubmit}
          >
            <div className="relative flex-1">
              {isNavigating ? (
                <Loader2 className="text-muted-foreground absolute top-1/2 left-3 size-5 -translate-y-1/2 animate-spin" />
              ) : (
                <Search className="text-muted-foreground absolute top-1/2 left-3 size-5 -translate-y-1/2" />
              )}
              <Input
                placeholder="Enter company / city / zip"
                className="h-12 border-0 pl-10 shadow-none focus-visible:border-0 focus-visible:ring-0 md:text-lg"
                value={query}
                disabled={isNavigating}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (allItems.length > 0) setIsOpen(true)
                }}
                role="combobox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-activedescendant={activeItemId}
                aria-autocomplete="list"
              />
            </div>
          </form>
        </div>
      </PopoverAnchor>

      <PopoverContent
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-[var(--radix-popover-trigger-width)] border-2 p-0 shadow-lg"
      >
        {isOpen && (
          <div id={listboxId} role="listbox">
            {locationResults.length > 0 && (
              <div role="group" aria-label="Locations">
                <div className="wb-label-muted border-b px-3 py-2 font-mono font-bold">
                  Locations
                </div>
                {locationResults.map((feature, i) => (
                  <button
                    key={feature.id}
                    id={`${listboxId}-option-${i}`}
                    role="option"
                    aria-selected={activeIndex === i}
                    className={cn(
                      "hover:bg-accent flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
                      activeIndex === i && "bg-accent"
                    )}
                    onMouseDown={() =>
                      navigateToItem({ type: "location", data: feature })
                    }
                    onMouseEnter={() => setActiveIndex(i)}
                  >
                    <MapPin className="text-muted-foreground size-4 shrink-0" />
                    {feature.place_name.replace(/, United States$/, "")}
                  </button>
                ))}
              </div>
            )}

            {companyResults.length > 0 && (
              <div role="group" aria-label="Companies">
                <div className="wb-label-muted border-b px-3 py-2 font-mono font-bold">
                  Companies
                </div>
                {companyResults.map((company, i) => {
                  const idx = locationResults.length + i
                  return (
                    <button
                      key={company.longSlug}
                      id={`${listboxId}-option-${idx}`}
                      role="option"
                      aria-selected={activeIndex === idx}
                      className={cn(
                        "hover:bg-accent flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
                        activeIndex === idx && "bg-accent"
                      )}
                      onMouseDown={() =>
                        navigateToItem({ type: "company", data: company })
                      }
                      onMouseEnter={() => setActiveIndex(idx)}
                    >
                      <Building2 className="text-muted-foreground size-4 shrink-0" />
                      {company.name}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {(hasNoResults || !!errorMessage) && (
          <div className="p-4 text-center">
            <p className="text-muted-foreground text-sm">
              {errorMessage ??
                "No results found. Check your spelling, or try another city or 5-digit zip code."}
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
